import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Tab, Tabs, ProgressBar, Modal } from 'react-bootstrap';
import { 
  FaMicrochip, 
  FaAtom, 
  FaRocket, 
  FaShieldAlt, 
  FaFlask, 
  FaChartLine, 
  FaBrain, 
  FaCloudSun, 
  FaInfinity,
  FaLightbulb,
  FaGlobe,
  FaUsers,
  FaAward,
  FaPlay,
  FaDownload,
  FaArrowRight,
  FaStar,
  FaCheck
} from 'react-icons/fa';

const QuantumComputingPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showModal, setShowModal] = useState(false);
  const [animatedStats, setAnimatedStats] = useState({ qubits: 0, speedup: 0, problems: 0 });

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedStats({ qubits: 1000, speedup: 1000000, problems: 250 });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const applications = [
    {
      icon: <FaShieldAlt />,
      title: "Cryptography & Security",
      description: "Revolutionary quantum encryption protocols that are theoretically unbreakable, protecting sensitive data with quantum key distribution and post-quantum cryptographic algorithms.",
      color: "#dc3545"
    },
    {
      icon: <FaFlask />,
      title: "Drug Discovery & Molecular Modeling",
      description: "Accelerate pharmaceutical research by simulating molecular interactions at the quantum level, reducing drug development time from decades to years.",
      color: "#28a745"
    },
    {
      icon: <FaChartLine />,
      title: "Financial Risk Analysis",
      description: "Optimize investment portfolios and assess financial risks with unprecedented accuracy using quantum algorithms for Monte Carlo simulations.",
      color: "#ffc107"
    },
    {
      icon: <FaBrain />,
      title: "AI & Machine Learning",
      description: "Supercharge artificial intelligence with quantum machine learning algorithms that can process vast datasets and identify patterns impossible for classical computers.",
      color: "#17a2b8"
    },
    {
      icon: <FaCloudSun />,
      title: "Climate & Weather Modeling",
      description: "Create highly accurate climate models and weather predictions by processing massive amounts of atmospheric data with quantum computational power.",
      color: "#6f42c1"
    },
    {
      icon: <FaRocket />,
      title: "Space Exploration",
      description: "Optimize spacecraft trajectories, analyze cosmic data, and solve complex astrophysics problems to advance our understanding of the universe.",
      color: "#fd7e14"
    }
  ];

  const benefits = [
    {
      icon: <FaInfinity />,
      title: "Exponential Speedup",
      description: "Solve specific computational problems millions of times faster than classical computers"
    },
    {
      icon: <FaShieldAlt />,
      title: "Quantum Security",
      description: "Unbreakable encryption methods based on fundamental laws of quantum physics"
    },
    {
      icon: <FaLightbulb />,
      title: "Scientific Breakthroughs",
      description: "Enable discoveries in physics, chemistry, and biology previously thought impossible"
    },
    {
      icon: <FaGlobe />,
      title: "Global Impact",
      description: "Transform industries from healthcare to finance, creating new economic opportunities"
    }
  ];

  const milestones = [
    { year: "2023", achievement: "Achieved 1000+ qubit quantum processor", status: "completed" },
    { year: "2024", achievement: "Quantum advantage in optimization problems", status: "completed" },
    { year: "2025", achievement: "First commercial quantum cloud platform", status: "current" },
    { year: "2026", achievement: "Quantum error correction breakthrough", status: "planned" },
    { year: "2027", achievement: "Universal quantum computer prototype", status: "planned" }
  ];

  const teamMembers = [
    { name: "Dr. Sarah Chen", role: "Chief Quantum Officer", expertise: "Quantum Algorithms" },
    { name: "Prof. Michael Rodriguez", role: "Head of Quantum Hardware", expertise: "Superconducting Qubits" },
    { name: "Dr. Aisha Patel", role: "Quantum Software Lead", expertise: "Quantum Programming" },
    { name: "Dr. James Thompson", role: "Quantum Applications Director", expertise: "Industry Solutions" }
  ];

  return (
    <div className="quantum-computing-page" style={{ background: 'linear-gradient(135deg,rgb(255, 255rgb(255, 255, 255)%,rgb(255, 255, 255) 100%)', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div className="hero-section py-5" style={{ background: 'linear-gradient(rgb(0, 0, 0), rgb(0, 0, 0))', color: 'white' }}>
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col lg={6}>
              <div className="hero-content">
                <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                  <FaAtom className="me-2" />
                  Next-Generation Technology
                </Badge>
                <h1 className="display-3 fw-bold mb-4">
                  Quantum Computing
                  <span className="d-block text-warning">Revolution</span>
                </h1>
                <p className="lead mb-4">
                  Step into the quantum realm where we're building the future of computation. 
                  Our cutting-edge quantum systems harness the mysterious power of quantum mechanics 
                  to solve humanity's most complex challenges.
                </p>
                <div className="d-flex gap-3 mb-4">
                  <Button 
                    variant="warning" 
                    size="lg" 
                    className="px-4"
                    onClick={() => setShowModal(true)}
                  >
                    <FaPlay className="me-2" />
                    Watch Demo
                  </Button>
                  <Button variant="outline-light" size="lg" className="px-4">
                    <FaDownload className="me-2" />
                    Get Started
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-stats">
                <Row>
                  <Col md={4}>
                    <div className="stat-card text-center p-4 bg-dark bg-opacity-50 rounded-3 mb-3">
                      <FaMicrochip className="display-4 text-warning mb-2" />
                      <h3 className="fw-bold">{animatedStats.qubits.toLocaleString()}+</h3>
                      <p className="mb-0">Qubits</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="stat-card text-center p-4 bg-dark bg-opacity-50 rounded-3 mb-3">
                      <FaRocket className="display-4 text-success mb-2" />
                      <h3 className="fw-bold">{animatedStats.speedup.toLocaleString()}x</h3>
                      <p className="mb-0">Speedup</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="stat-card text-center p-4 bg-dark bg-opacity-50 rounded-3 mb-3">
                      <FaLightbulb className="display-4 text-info mb-2" />
                      <h3 className="fw-bold">{animatedStats.problems}+</h3>
                      <p className="mb-0">Problems Solved</p>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="py-5">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-5 nav-pills"
          fill
        >
          <Tab eventKey="overview" title="Overview">
            <Card className="shadow-lg border-0 bg-light">
              <Card.Body className="p-5">
                <Row>
                  <Col lg={8}>
                    <h2 className="fw-bold mb-4 text-primary">
                      <FaAtom className="me-3" />
                      The Quantum Advantage
                    </h2>
                    <p className="lead mb-4">
                      Quantum computing represents the most significant leap in computational power since the invention 
                      of the transistor. By harnessing quantum mechanical phenomena like superposition and entanglement, 
                      our quantum systems can process information in ways that defy classical physics.
                    </p>
                    <p className="mb-4">
                      Unlike classical bits that exist in either 0 or 1 states, quantum bits (qubits) can exist in 
                      multiple states simultaneously. This quantum superposition, combined with quantum entanglement, 
                      allows our quantum computers to explore vast solution spaces in parallel, achieving exponential 
                      speedups for specific problem classes.
                    </p>
                    <div className="quantum-principles">
                      <h4 className="fw-bold mb-3">Core Quantum Principles</h4>
                      <Row>
                        <Col md={6}>
                          <div className="principle-card p-3 bg-primary bg-opacity-10 rounded-3 mb-3">
                            <h6 className="fw-bold text-primary">Superposition</h6>
                            <p className="mb-0 small">Qubits can exist in multiple states simultaneously</p>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="principle-card p-3 bg-success bg-opacity-10 rounded-3 mb-3">
                            <h6 className="fw-bold text-success">Entanglement</h6>
                            <p className="mb-0 small">Qubits can be correlated across vast distances</p>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="principle-card p-3 bg-warning bg-opacity-10 rounded-3 mb-3">
                            <h6 className="fw-bold text-warning">Interference</h6>
                            <p className="mb-0 small">Wave-like properties enable computation</p>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="principle-card p-3 bg-info bg-opacity-10 rounded-3 mb-3">
                            <h6 className="fw-bold text-info">Decoherence</h6>
                            <p className="mb-0 small">Managing quantum state stability</p>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col lg={4}>
                    <div className="quantum-visual p-4 bg-dark text-white rounded-3">
                      <h5 className="fw-bold mb-3">
                        <FaInfinity className="me-2" />
                        Quantum vs Classical
                      </h5>
                      <div className="comparison mb-3">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Processing Power</span>
                          <Badge bg="success">Quantum</Badge>
                        </div>
                        <ProgressBar variant="success" now={95} className="mb-3" />
                        <div className="d-flex justify-content-between mb-2">
                          <span>Classical Limit</span>
                          <Badge bg="secondary">Traditional</Badge>
                        </div>
                        <ProgressBar variant="secondary" now={15} />
                      </div>
                      <p className="small mb-0">
                        Our quantum systems can solve certain problems that would take classical 
                        computers longer than the age of the universe.
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="applications" title="Applications">
            <div className="applications-section">
              <h2 className="text-center fw-bold mb-5 text-primary">Quantum Applications</h2>
              <Row>
                {applications.map((app, index) => (
                  <Col lg={4} md={6} key={index} className="mb-4">
                    <Card className="h-100 shadow-sm border-0 hover-card">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div 
                            className="icon-circle me-3 d-flex align-items-center justify-content-center"
                            style={{ 
                              width: '60px', 
                              height: '60px', 
                              backgroundColor: app.color, 
                              borderRadius: '50%',
                              color: 'white',
                              fontSize: '1.5rem'
                            }}
                          >
                            {app.icon}
                          </div>
                          <h5 className="fw-bold mb-0">{app.title}</h5>
                        </div>
                        <p className="text-muted mb-3">{app.description}</p>
                        <Button variant="outline-primary" size="sm">
                          Learn More <FaArrowRight className="ms-1" />
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </Tab>

          <Tab eventKey="benefits" title="Benefits">
            <div className="benefits-section">
              <Row>
                <Col lg={6}>
                  <h2 className="fw-bold mb-4 text-primary">Why Quantum Computing Matters</h2>
                  <p className="lead mb-4">
                    Quantum computing isn't just faster computing—it's a fundamentally different approach 
                    to solving problems that opens up entirely new possibilities for science, industry, and society.
                  </p>
                  {benefits.map((benefit, index) => (
                    <div key={index} className="benefit-item d-flex mb-4">
                      <div className="benefit-icon me-3">
                        <div 
                          className="icon-circle d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            backgroundColor: '#3b82f6', 
                            borderRadius: '50%',
                            color: 'white'
                          }}
                        >
                          {benefit.icon}
                        </div>
                      </div>
                      <div className="benefit-content">
                        <h5 className="fw-bold mb-2">{benefit.title}</h5>
                        <p className="text-muted mb-0">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </Col>
                <Col lg={6}>
                  <div className="quantum-impact-chart p-4 bg-light rounded-3">
                    <h4 className="fw-bold mb-4">Market Impact Projection</h4>
                    <div className="impact-metrics">
                      <div className="metric mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-bold">Healthcare Revolution</span>
                          <span>85%</span>
                        </div>
                        <ProgressBar variant="success" now={85} />
                      </div>
                      <div className="metric mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-bold">Financial Optimization</span>
                          <span>78%</span>
                        </div>
                        <ProgressBar variant="warning" now={78} />
                      </div>
                      <div className="metric mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-bold">Cybersecurity Enhancement</span>
                          <span>92%</span>
                        </div>
                        <ProgressBar variant="danger" now={92} />
                      </div>
                      <div className="metric mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span className="fw-bold">AI Acceleration</span>
                          <span>88%</span>
                        </div>
                        <ProgressBar variant="info" now={88} />
                      </div>
                    </div>
                    <p className="small text-muted mt-3">
                      <FaStar className="text-warning me-1" />
                      Projected impact by 2030 across key industries
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          </Tab>

          <Tab eventKey="roadmap" title="Roadmap">
            <div className="roadmap-section">
              <h2 className="text-center fw-bold mb-5 text-primary">Our Quantum Journey</h2>
              <div className="timeline">
                {milestones.map((milestone, index) => (
                  <div key={index} className="timeline-item mb-4">
                    <Row className="align-items-center">
                      <Col md={2}>
                        <div className="timeline-year">
                          <Badge 
                            bg={milestone.status === 'completed' ? 'success' : 
                                milestone.status === 'current' ? 'warning' : 'primary'}
                            className="fs-6 px-3 py-2"
                          >
                            {milestone.year}
                          </Badge>
                        </div>
                      </Col>
                      <Col md={8}>
                        <Card className={`border-0 ${milestone.status === 'current' ? 'bg-warning bg-opacity-10' : 'bg-light'}`}>
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                              {milestone.status === 'completed' && <FaCheck className="text-success me-2" />}
                              {milestone.status === 'current' && <FaRocket className="text-warning me-2" />}
                              {milestone.status === 'planned' && <FaLightbulb className="text-primary me-2" />}
                              <span className="fw-bold">{milestone.achievement}</span>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={2}>
                        <Badge 
                          bg={milestone.status === 'completed' ? 'success' : 
                              milestone.status === 'current' ? 'warning' : 'secondary'}
                          className="text-capitalize"
                        >
                          {milestone.status}
                        </Badge>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            </div>
          </Tab>

          <Tab eventKey="team" title="Team">
            <div className="team-section">
              <h2 className="text-center fw-bold mb-5 text-primary">Quantum Pioneers</h2>
              <p className="text-center lead mb-5">
                Meet the brilliant minds pushing the boundaries of quantum computing and shaping the future of technology.
              </p>
              <Row>
                {teamMembers.map((member, index) => (
                  <Col lg={3} md={6} key={index} className="mb-4">
                    <Card className="h-100 shadow-sm border-0 text-center">
                      <Card.Body className="p-4">
                        <div 
                          className="team-avatar mx-auto mb-3 d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            backgroundColor: '#3b82f6', 
                            borderRadius: '50%',
                            color: 'white',
                            fontSize: '2rem'
                          }}
                        >
                          <FaUsers />
                        </div>
                        <h5 className="fw-bold mb-1">{member.name}</h5>
                        <p className="text-primary mb-2">{member.role}</p>
                        <Badge bg="light" text="dark" className="mb-2">
                          {member.expertise}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </Tab>
        </Tabs>

        {/* Call to Action */}
        <div className="cta-section py-5 mt-5">
          <Card className="bg-white text-dark border-0 shadow-lg">
            <Card.Body className="p-5 text-center">
              <FaRocket className="display-4 mb-4" />
              <h2 className="fw-bold mb-3">Ready to Enter the Quantum Era?</h2>
              <p className="lead mb-4">
                Join us in revolutionizing computation and solving the world's most complex challenges. 
                The quantum future starts now.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button variant="outline-success" size="lg" className="px-4">
                  <FaAward className="me-2" />
                  Partner With Us
                </Button>
                <Button variant="outline-dark" size="lg" className="px-4">
                  <FaGlobe className="me-2" />
                  Explore Solutions
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* Demo Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaPlay className="me-2" />
            Quantum Computing Demo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="demo-placeholder text-center py-5">
            <FaMicrochip className="display-1 text-primary mb-4" />
            <h4 className="fw-bold mb-3">Experience Quantum Computing</h4>
            <p className="text-muted mb-4">
              Watch our quantum processor solve a complex optimization problem in real-time, 
              demonstrating the power of quantum superposition and entanglement.
            </p>
            <Button variant="primary" size="lg">
              <FaPlay className="me-2" />
              Start Demo
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <style jsx>{`
        .hover-card {
          transition: all 0.3s ease;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .timeline-item {
          position: relative;
        }
        .timeline-item:not(:last-child)::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 100%;
          width: 2px;
          height: 20px;
          background: #dee2e6;
          transform: translateX(-50%);
        }
        @media (max-width: 768px) {
          .timeline-item::after {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default QuantumComputingPage;